import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Building2, Key, ShoppingCart, Wrench, CheckCircle, Zap, Users, Globe,
  AlertTriangle, MailWarning, MailCheck, Percent, FileSearch, CalendarCheck,
  CalendarX, Send, ListChecks, FolderClock, Hourglass, PlugZap, Camera,
  GripVertical, Settings2, RotateCcw, Check,
} from "lucide-react";
import { useBiens } from "@/hooks/useBiens";
import { useProfiles } from "@/hooks/useProfiles";
import type { Bien } from "@/types/bien";
import { matchView, tauxRemplissage, viewToUrl, type DashboardView, type ViewTargetPage } from "@/lib/dashboardViews";
import { cn } from "@/lib/utils";

// ─── UI atoms ────────────────────────────────────────────────────────────────

type Tone = "default" | "warning" | "danger" | "success" | "info";

function toneClass(tone: Tone): string {
  return tone === "danger"  ? "text-danger"
       : tone === "warning" ? "text-warning"
       : tone === "success" ? "text-success"
       : tone === "info"    ? "text-accent"
       :                      "text-text-primary";
}

function StatCard({
  icon: Icon,
  label,
  value,
  tone = "default",
  to,
  suffix,
  secondary,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: number | string;
  tone?: Tone;
  to?: string;
  /** Texte affiché en petit après la valeur (ex: "%" pour le taux) */
  suffix?: string;
  /** Métrique complémentaire à droite (ex: taux occupé en vert quand on
   *  affiche le taux vacant en rouge en grand). */
  secondary?: { value: number | string; suffix?: string; tone: Tone };
}) {
  const content = (
    <>
      <div className="flex items-center gap-2 text-xs font-medium tracking-wider uppercase text-text-muted">
        <Icon size={14} className="text-text-muted" />
        <span>{label}</span>
      </div>
      <div className={`text-3xl font-semibold ${toneClass(tone)} flex items-baseline gap-1`}>
        <span>{value}</span>
        {suffix && <span className="text-base text-text-muted font-normal">{suffix}</span>}
        {secondary && (
          <span className={`ml-auto text-base font-medium ${toneClass(secondary.tone)}`}>
            {secondary.value}{secondary.suffix && <span className="text-text-muted font-normal">{secondary.suffix}</span>}
          </span>
        )}
      </div>
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className="h-full bg-surface border border-border rounded-lg p-5 flex flex-col gap-3 hover:border-accent transition-colors cursor-pointer outline-none"
      >
        {content}
      </Link>
    );
  }

  return (
    <div className="h-full bg-surface border border-border rounded-lg p-5 flex flex-col gap-3">
      {content}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xs font-semibold tracking-widest uppercase text-text-secondary mb-4">
      {children}
    </h2>
  );
}

// ─── Section 1 registry — 14 tuiles "Vue générale", ordre par défaut ─────────
//
// Demande user 2026-05-08 : permettre de réordonner les tuiles via drag-and-drop.
// L'ordre custom est persisté en localStorage (par device, pas cross-device pour
// l'instant — pattern aligné sur le picker de colonnes /biens, où on a démarré
// localStorage puis ajouté Supabase). Si on veut cross-device : ajouter
// `profiles.prefs_dashboard_section1_order jsonb` (cf. migration 043 pour pattern).
//
// Le registry sert d'unique source : ordre par défaut + métadonnées de rendu.
// `tone` peut être un Tone simple ou "info" pour le taux. `view` est la clé
// matchView (sauf taux qui pointe vers "vides" pour le filtre cohérent CEO).

type Section1TileId =
  | "all" | "prep" | "dispo" | "loue" | "vendu"
  | "peb-absent-all" | "peb-absent-prep" | "peb-absent-active" | "peb-expirant"
  | "dispo-1m" | "dispo-3m" | "dispo-6m"
  | "incomplete" | "mailing-ok" | "taux" | "els-cloture-pending" | "els-final-closure-pending";

interface Section1Tile {
  id: Section1TileId;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  labelKey: string;
  view: DashboardView;     // pour le compteur ET la nav (sauf "taux", traité à part)
  tone: Tone;
  /** Si true, on lit `taux` au lieu de `count[view]` et on affiche le suffixe %. */
  isTaux?: boolean;
  /** Page cible du clic (par défaut "biens"). Demande user 2026-05-08 :
   *  certaines tuiles redirigent vers /suivi-els ou /apparts-for-rent. */
  targetPage?: ViewTargetPage;
}

const SECTION_1_DEFAULT: Section1Tile[] = [
  { id: "all",                      icon: Building2,    labelKey: "dashboard.stats.totalBiens",            view: "all",                       tone: "default" },
  { id: "prep",                     icon: Wrench,       labelKey: "dashboard.stats.enPreparation",         view: "prep",                      tone: "warning" },
  { id: "dispo",                    icon: CheckCircle,  labelKey: "dashboard.stats.disponibles",           view: "dispo",                     tone: "success" },
  { id: "loue",                     icon: Key,          labelKey: "dashboard.stats.loues",                 view: "loue",                      tone: "default" },
  { id: "vendu",                    icon: ShoppingCart, labelKey: "dashboard.stats.vendus",                view: "vendu",                     tone: "default" },
  { id: "peb-absent-all",           icon: Zap,          labelKey: "dashboard.stats.pebAbsent",             view: "peb-absent-all",            tone: "danger"  },
  { id: "peb-absent-prep",          icon: Zap,          labelKey: "dashboard.stats.pebAbsentPrep",         view: "peb-absent-prep",           tone: "danger", targetPage: "suivi-els"  },
  { id: "peb-absent-active",        icon: Zap,          labelKey: "dashboard.stats.pebAbsentActive",       view: "peb-absent-active",         tone: "danger", targetPage: "suivi-els"  },
  { id: "peb-expirant",             icon: Zap,          labelKey: "dashboard.stats.pebExpirant",           view: "peb-expirant",              tone: "warning" },
  { id: "dispo-1m",                 icon: Hourglass,    labelKey: "dashboard.stats.dispo1m",               view: "dispo-1m",                  tone: "default" },
  { id: "dispo-3m",                 icon: Hourglass,    labelKey: "dashboard.stats.dispo3m",               view: "dispo-3m",                  tone: "warning" },
  { id: "dispo-6m",                 icon: Hourglass,    labelKey: "dashboard.stats.dispo6m",               view: "dispo-6m",                  tone: "danger"  },
  { id: "incomplete",               icon: MailWarning,  labelKey: "dashboard.stats.incompletsMailing",     view: "incomplete",                tone: "danger"  },
  { id: "mailing-ok",               icon: MailCheck,    labelKey: "dashboard.stats.mailingOk",             view: "mailing-ok",                tone: "success", targetPage: "apparts-for-rent" },
  { id: "taux",                     icon: Percent,      labelKey: "dashboard.stats.videLocatif",           view: "vides",                     tone: "danger", isTaux: true },
  { id: "els-cloture-pending",      icon: FileSearch,   labelKey: "dashboard.stats.elsCloturePending",     view: "els-cloture-pending",       tone: "danger", targetPage: "suivi-els"  },
  { id: "els-final-closure-pending",icon: FolderClock,  labelKey: "dashboard.stats.elsFinalClosurePending",view: "els-final-closure-pending", tone: "warning", targetPage: "suivi-els" },
];

const DEFAULT_TILE_IDS: Section1TileId[] = SECTION_1_DEFAULT.map((t) => t.id);
const TILE_REGISTRY = new Map(SECTION_1_DEFAULT.map((t) => [t.id, t]));

const ORDER_LS_KEY = "skyline-dashboard-section1-order";

function loadOrderFromLs(): Section1TileId[] {
  try {
    const raw = localStorage.getItem(ORDER_LS_KEY);
    if (!raw) return [...DEFAULT_TILE_IDS];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [...DEFAULT_TILE_IDS];
    // Filtre les ids invalides (registry évolue) et complète avec les manquants
    // pour qu'un ajout de tuile dans le code apparaisse même chez les users qui
    // ont déjà customisé leur ordre.
    const known = parsed.filter((id): id is Section1TileId => typeof id === "string" && TILE_REGISTRY.has(id as Section1TileId));
    const missing = DEFAULT_TILE_IDS.filter((id) => !known.includes(id));
    return [...known, ...missing];
  } catch {
    return [...DEFAULT_TILE_IDS];
  }
}

function saveOrderToLs(order: Section1TileId[]) {
  try {
    localStorage.setItem(ORDER_LS_KEY, JSON.stringify(order));
  } catch {
    /* quota / privacy mode → on ignore silencieusement */
  }
}

// ─── Main dashboard ──────────────────────────────────────────────────────────

export function CLevelDashboard() {
  const { t } = useTranslation();
  const { data: biensData } = useBiens();
  const { data: profilesData } = useProfiles();
  const biens = useMemo(() => (biensData ?? []) as unknown as Bien[], [biensData]);

  // ── Section 1 : counts via matchView (un seul passage sur biens) ─────────
  // Centralisé pour garantir l'alignement avec le filtre /biens?view=<key>.
  const count = useMemo(() => {
    const c: Record<DashboardView, number> = {
      all: 0, prep: 0, dispo: 0, loue: 0, vendu: 0, vides: 0,
      "peb-absent-all": 0, "peb-absent-prep": 0, "peb-absent-active": 0, "peb-expirant": 0,
      "dispo-1m": 0, "dispo-3m": 0, "dispo-6m": 0,
      incomplete: 0, "mailing-ok": 0, "els-cloture-pending": 0, "els-final-closure-pending": 0,
      "els-avenir-ok-collab": 0, "els-avenir-issue-collab": 0, "els-cloture-pending-collab": 0,
      "els-final-closure-pending-collab": 0, "peb-a-gerer-collab": 0,
      "transfert-pas-fait-collab": 0, "photos-pas-pret-collab": 0,
      "published-active": 0, "draft-active": 0, "published-stale-active": 0,
    };
    const sectionOneViews: DashboardView[] = [
      "all", "prep", "dispo", "loue", "vendu", "vides",
      "peb-absent-all", "peb-absent-prep", "peb-absent-active", "peb-expirant",
      "dispo-1m", "dispo-3m", "dispo-6m",
      "incomplete", "mailing-ok", "els-cloture-pending", "els-final-closure-pending",
      "published-active", "draft-active", "published-stale-active",
    ];
    for (const b of biens) {
      for (const v of sectionOneViews) {
        if (matchView(b, v)) c[v]++;
      }
    }
    return c;
  }, [biens]);

  const taux = useMemo(() => tauxRemplissage(biens), [biens]);

  // ── Section 1 : ordre custom + drag-and-drop ─────────────────────────────
  const [order, setOrder] = useState<Section1TileId[]>(() => loadOrderFromLs());
  const [editing, setEditing] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  const onDragStart = (e: React.DragEvent, idx: number) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
  };
  const onDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropIndex !== idx) setDropIndex(idx);
  };
  const onDrop = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDropIndex(null);
    if (dragIndex === null || dragIndex === idx) {
      setDragIndex(null);
      return;
    }
    setOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      const insertAt = dragIndex < idx ? idx - 1 : idx;
      next.splice(insertAt, 0, moved);
      saveOrderToLs(next);
      return next;
    });
    setDragIndex(null);
  };
  const onDragEnd = () => {
    setDragIndex(null);
    setDropIndex(null);
  };
  const resetOrder = () => {
    const fresh = [...DEFAULT_TILE_IDS];
    setOrder(fresh);
    saveOrderToLs(fresh);
  };

  // ── Section 2 : ELS par collaborateur ────────────────────────────────────
  const collaborateurs = useMemo(() => {
    return (profilesData ?? [])
      .filter((p) => p.role === "collaborateur" && p.statut !== "inactif")
      .map((p) => ({
        id: p.id,
        name: `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email,
      }));
  }, [profilesData]);

  const elsByCollab = useMemo(() => {
    return collaborateurs.map((collab) => {
      const ctx = { collabId: collab.id };
      let avenirOk = 0, avenirIssue = 0, cloturePending = 0, finalClosurePending = 0, pebAGerer = 0, transfertPasFait = 0, photosPasPret = 0;
      for (const b of biens) {
        if (matchView(b, "els-avenir-ok-collab", ctx)) avenirOk++;
        if (matchView(b, "els-avenir-issue-collab", ctx)) avenirIssue++;
        if (matchView(b, "els-cloture-pending-collab", ctx)) cloturePending++;
        if (matchView(b, "els-final-closure-pending-collab", ctx)) finalClosurePending++;
        if (matchView(b, "peb-a-gerer-collab", ctx)) pebAGerer++;
        if (matchView(b, "transfert-pas-fait-collab", ctx)) transfertPasFait++;
        if (matchView(b, "photos-pas-pret-collab", ctx)) photosPasPret++;
      }
      return { ...collab, avenirOk, avenirIssue, cloturePending, finalClosurePending, pebAGerer, transfertPasFait, photosPasPret };
    });
  }, [biens, collaborateurs]);

  return (
    <div className="flex flex-col gap-8">
      {/* ── Section 1 — Vue générale ───────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <SectionTitle>{t("dashboard.sections.general")}</SectionTitle>
          <div className="flex items-center gap-1.5 -mt-3">
            {editing && (
              <button
                type="button"
                onClick={resetOrder}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-border bg-surface text-text-secondary hover:text-text-primary hover:border-text-secondary transition-colors cursor-pointer outline-none"
                title={t("dashboard.section1.reset")}
              >
                <RotateCcw size={12} />
                <span>{t("dashboard.section1.reset")}</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setEditing((e) => !e)}
              className={cn(
                "inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border bg-surface transition-colors cursor-pointer outline-none",
                editing
                  ? "border-accent text-accent"
                  : "border-border text-text-secondary hover:text-text-primary hover:border-text-secondary"
              )}
            >
              {editing ? <Check size={12} /> : <Settings2 size={12} />}
              <span>{editing ? t("dashboard.section1.done") : t("dashboard.section1.personalize")}</span>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 auto-rows-fr">
          {order.map((id, idx) => {
            const tile = TILE_REGISTRY.get(id);
            if (!tile) return null;
            // Tuile "vide locatif" (demande user 2026-05-08) : taux vacant en
            // grand rouge (100 - taux), nombre absolu de biens vides en plus
            // petit rouge à droite. L'attention CEO se porte sur la douleur
            // (vacance) — sous deux angles complémentaires : ratio + volume.
            const value = tile.isTaux ? 100 - taux : count[tile.view];
            const suffix = tile.isTaux ? "%" : undefined;
            const secondary = tile.isTaux
              ? { value: count["vides"], tone: "danger" as Tone }
              : undefined;
            const isDragging = dragIndex === idx;
            const isDropTarget = dropIndex === idx && dragIndex !== null && dragIndex !== idx;
            const card = (
              <StatCard
                icon={tile.icon}
                label={t(tile.labelKey)}
                value={value}
                tone={tile.tone}
                suffix={suffix}
                secondary={secondary}
                to={editing ? undefined : viewToUrl(tile.view, { page: tile.targetPage })}
              />
            );
            if (!editing) {
              return <div key={id} className="h-full">{card}</div>;
            }
            return (
              <div
                key={id}
                draggable
                onDragStart={(e) => onDragStart(e, idx)}
                onDragOver={(e) => onDragOver(e, idx)}
                onDrop={(e) => onDrop(e, idx)}
                onDragEnd={onDragEnd}
                className={cn(
                  "relative h-full cursor-grab active:cursor-grabbing select-none transition-opacity",
                  isDragging && "opacity-40",
                  isDropTarget && "ring-2 ring-accent rounded-lg",
                )}
              >
                {card}
                <span className="absolute top-2 right-2 text-text-muted bg-surface/80 rounded p-0.5 pointer-events-none">
                  <GripVertical size={14} />
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Section 2 — ELS par collaborateur ───────────────────────────────── */}
      <section>
        <SectionTitle>
          <span className="inline-flex items-center gap-2">
            <Users size={14} className="text-text-muted" /> {t("dashboard.sections.els")}
          </span>
        </SectionTitle>
        {elsByCollab.length === 0 ? (
          <p className="text-sm text-text-muted">{t("dashboard.els.noCollaborateur")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {elsByCollab.map((c) => (
              <div key={c.id} className="bg-surface border border-border rounded-lg p-5">
                <div className="text-sm font-semibold text-text-primary mb-3">
                  {c.name}
                </div>
                <div className="flex flex-col gap-1.5 text-sm">
                  <CollabRow
                    icon={CalendarCheck}
                    label={t("dashboard.els.aVenirOk")}
                    value={c.avenirOk}
                    tone={c.avenirOk > 0 ? "success" : "muted"}
                    to={viewToUrl("els-avenir-ok-collab", { collabId: c.id })}
                  />
                  <CollabRow
                    icon={CalendarX}
                    label={t("dashboard.els.aVenirIssue")}
                    value={c.avenirIssue}
                    tone={c.avenirIssue > 0 ? "warning" : "muted"}
                    to={viewToUrl("els-avenir-issue-collab", { collabId: c.id })}
                  />
                  <CollabRow
                    icon={FileSearch}
                    label={t("dashboard.els.cloturePending")}
                    value={c.cloturePending}
                    tone={c.cloturePending > 0 ? "danger" : "muted"}
                    to={viewToUrl("els-cloture-pending-collab", { collabId: c.id })}
                  />
                  <CollabRow
                    icon={FolderClock}
                    label={t("dashboard.els.finalClosurePending")}
                    value={c.finalClosurePending}
                    tone={c.finalClosurePending > 0 ? "warning" : "muted"}
                    to={viewToUrl("els-final-closure-pending-collab", { collabId: c.id })}
                  />
                  <CollabRow
                    icon={Zap}
                    label={t("dashboard.els.pebAGerer")}
                    value={c.pebAGerer}
                    tone={c.pebAGerer > 0 ? "warning" : "muted"}
                    to={viewToUrl("peb-a-gerer-collab", { collabId: c.id })}
                  />
                  <CollabRow
                    icon={PlugZap}
                    label={t("dashboard.els.transfertPasFait")}
                    value={c.transfertPasFait}
                    tone={c.transfertPasFait > 0 ? "warning" : "muted"}
                    to={viewToUrl("transfert-pas-fait-collab", { collabId: c.id })}
                  />
                  <CollabRow
                    icon={Camera}
                    label={t("dashboard.els.photosPasPret")}
                    value={c.photosPasPret}
                    tone={c.photosPasPret > 0 ? "warning" : "muted"}
                    to={viewToUrl("photos-pas-pret-collab", { collabId: c.id })}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Section 3 — Publication (sur biens EN PRÉP + DISPO uniquement) ──── */}
      <section>
        <SectionTitle>
          <span className="inline-flex items-center gap-2">
            <Globe size={14} className="text-text-muted" /> {t("dashboard.sections.publication")}
          </span>
        </SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-3xl">
          <StatCard icon={Send}          label={t("dashboard.stats.publiesActive")}   value={count["published-active"]}        tone="success" to={viewToUrl("published-active")} />
          <StatCard icon={AlertTriangle} label={t("dashboard.stats.brouillonsActive")} value={count["draft-active"]}            tone="warning" to={viewToUrl("draft-active")} />
          <StatCard icon={ListChecks}    label={t("dashboard.stats.publishedStale")}   value={count["published-stale-active"]} tone="warning" to={viewToUrl("published-stale-active")} />
        </div>
      </section>
    </div>
  );
}

// ─── Sub-component for collab rows ──────────────────────────────────────────

function CollabRow({
  icon: Icon,
  label,
  value,
  tone,
  to,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: number;
  tone: "success" | "warning" | "danger" | "muted";
  to: string;
}) {
  const { t } = useTranslation();
  const toneCls =
    tone === "danger"  ? "text-danger"  :
    tone === "warning" ? "text-warning" :
    tone === "success" ? "text-success" :
                         "text-text-muted";
  return (
    <Link
      to={to}
      className="flex items-center justify-between -mx-2 px-2 py-1 rounded hover:bg-surface-2 transition-colors cursor-pointer outline-none"
    >
      <span className="inline-flex items-center gap-2 text-text-secondary">
        <Icon size={13} className="text-text-muted" />
        {label}
      </span>
      <span className={`font-semibold ${toneCls}`}>
        {t("dashboard.els.bien", { count: value })}
      </span>
    </Link>
  );
}
